import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/App";
import { Card } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Users, Package, ShoppingCart, DollarSign, Truck, MapPin, CheckSquare, ChevronDown, AlertCircle, Bell, Sparkles, ArrowRight, Check, X, Wallet, TrendingUp, TrendingDown, Settings as SettingsIcon } from "lucide-react";
import { toast } from "sonner";
import { PageLoader } from "@/components/Spinner";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  CartesianGrid,
  Area,
  AreaChart,
} from "recharts";
import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip as LeafletTooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { CANTON_COORDS, COSTA_RICA_CENTER, COSTA_RICA_ZOOM } from "@/data/cantonCoords";
import { formatMoney, formatMoneyCompact } from "@/lib/money";
import WelcomeQuickStart from "@/components/WelcomeQuickStart";
import OnboardingWizard from "@/components/OnboardingWizard";

const COLORS = ["#002FA7", "#FFCC00", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4", "#EC4899"];

/**
 * Small pill that surfaces wizard completion in the dashboard header.
 *
 * Counts how many of the 4 wizard checkpoints are done:
 *   1. Empresa: nombre_registrado present.
 *   2. CEDI: both cedi_lat and cedi_lng set.
 *   3. Logo: logo_url present.
 *   4. Datos: at least 1 customer imported.
 *
 * Renders one of three states:
 *   • All 4 done → green pill "✓ Configuración completa".
 *   • >0 done   → amber pill "X/4 configurado · Continuar" (opens wizard).
 *   • 0 done    → nothing (the welcome card already screams loud enough).
 */
function SetupCompleteBadge({ company, hasCustomers, onReopenWizard }) {
  if (!company) return null;
  const done = {
    empresa: Boolean((company.nombre_registrado || "").trim()),
    cedi: Boolean((company.cedi_maps_link || "").trim()),
    logo: Boolean(company.logo_url),
    datos: Boolean(hasCustomers),
  };
  const total = Object.values(done).filter(Boolean).length;
  if (total === 0) return null;
  const allDone = total === 4;
  return (
    <button
      type="button"
      onClick={onReopenWizard}
      data-testid="setup-complete-badge"
      data-state={allDone ? "complete" : "partial"}
      title={
        allDone
          ? "Empresa, CEDI, logo y datos iniciales configurados."
          : `Faltan: ${[
              !done.empresa && "datos de la empresa",
              !done.cedi    && "coordenadas del CEDI",
              !done.logo    && "logo",
              !done.datos   && "tus primeros clientes",
            ].filter(Boolean).join(" · ")}`
      }
      className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm transition-colors ${
        allDone
          ? "border border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
          : "border border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100"
      }`}
    >
      <span className={`h-4 w-4 rounded-sm grid place-items-center font-bold text-[10px] ${
        allDone ? "bg-emerald-600 text-white" : "bg-amber-500 text-white"
      }`}>
        {allDone ? "✓" : total}
      </span>
      {allDone ? "Configuración completa" : `${total}/4 configurado · Continuar`}
    </button>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [tasksOpen, setTasksOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [onboardingDismissed, setOnboardingDismissed] = useState(() => {
    try {
      const userId = JSON.parse(localStorage.getItem("user") || "{}")?.id;
      return userId ? localStorage.getItem(`onboarding_dismissed_${userId}`) === "1" : false;
    } catch {
      return false;
    }
  });
  const [graduatedSeen, setGraduatedSeen] = useState(() => {
    try {
      const userId = JSON.parse(localStorage.getItem("user") || "{}")?.id;
      return userId ? localStorage.getItem(`onboarding_graduated_seen_${userId}`) === "1" : false;
    } catch {
      return false;
    }
  });
  // OnboardingWizard state. Auto-opens for tenants that haven't completed
  // it yet AND don't already have a company name configured. Manual
  // re-open available via the "Configuración guiada" pill in the WelcomeCard.
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardCompany, setWizardCompany] = useState(null);

  const dismissOnboarding = () => {
    try {
      const userId = JSON.parse(localStorage.getItem("user") || "{}")?.id;
      if (userId) localStorage.setItem(`onboarding_dismissed_${userId}`, "1");
    } catch { /* ignore */ }
    setOnboardingDismissed(true);
  };

  const markGraduatedSeen = () => {
    try {
      const userId = JSON.parse(localStorage.getItem("user") || "{}")?.id;
      if (userId) localStorage.setItem(`onboarding_graduated_seen_${userId}`, "1");
    } catch { /* ignore */ }
    setGraduatedSeen(true);
  };

  useEffect(() => {
    fetchStats();
    fetchTasks();
    // Auto-open wizard for fresh accounts. Heuristic: company has no name
    // AND user hasn't dismissed the wizard previously. Race-safe: we still
    // fetch /company once to populate wizardCompany.
    (async () => {
      try {
        const { data } = await api.get("/company");
        setWizardCompany(data);
        const done = localStorage.getItem("onboarding_wizard_done") === "1";
        const isNew = !(data?.nombre_registrado || "").trim();
        if (isNew && !done) {
          setWizardOpen(true);
        }
      } catch { /* ignore — non-fatal */ }
    })();
  }, []);

  // Auto-dismiss the graduated celebration card after 8s.
  useEffect(() => {
    if (!stats) return;
    const done =
      (stats.total_customers || 0) > 0 &&
      (stats.total_products || 0) > 0 &&
      (stats.total_orders || 0) > 0;
    if (done && !graduatedSeen && !onboardingDismissed) {
      const t = setTimeout(markGraduatedSeen, 8000);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stats, graduatedSeen, onboardingDismissed]);

  const fetchStats = async () => {
    try {
      const response = await api.get("/dashboard");
      setStats(response.data);
    } catch (error) {
      toast.error("Error al cargar estadísticas");
    } finally {
      setLoading(false);
    }
  };

  const fetchTasks = async () => {
    try {
      const res = await api.get("/tasks");
      setTasks(res.data);
    } catch { /* ignore - module may be disabled */ }
  };

  const pendingTasks = useMemo(() => {
    const today = new Date().toISOString().split("T")[0];
    const urgOrd = { alta: 0, media: 1, baja: 2 };
    return tasks
      .filter(t => t.estado !== "completada")
      .sort((a, b) => {
        // Overdue first
        const aOverdue = a.fecha_vencimiento && a.fecha_vencimiento < today ? 0 : 1;
        const bOverdue = b.fecha_vencimiento && b.fecha_vencimiento < today ? 0 : 1;
        if (aOverdue !== bOverdue) return aOverdue - bOverdue;
        // Then by urgency
        if (urgOrd[a.urgencia] !== urgOrd[b.urgencia]) return urgOrd[a.urgencia] - urgOrd[b.urgencia];
        // Then by due date
        const aDate = a.fecha_vencimiento || "9999-12-31";
        const bDate = b.fecha_vencimiento || "9999-12-31";
        return aDate.localeCompare(bDate);
      });
  }, [tasks]);

  const tasksAlta = pendingTasks.filter(t => t.urgencia === "alta").length;

  if (loading) {
    return <PageLoader label="Cargando tu negocio…" testid="dashboard-loader" />;
  }

  const hasCustomers = (stats?.total_customers || 0) > 0;
  const hasProducts = (stats?.total_products || 0) > 0;
  const hasOrders = (stats?.total_orders || 0) > 0;
  const completedSteps = [hasCustomers, hasProducts, hasOrders].filter(Boolean).length;
  const showOnboarding = !onboardingDismissed && completedSteps < 3;
  const showGraduated = !onboardingDismissed && !graduatedSeen && completedSteps === 3;

  const topCantones = (stats?.customers_by_canton || []).slice(0, 10);
  const provinciasData = stats?.customers_by_provincia || [];
  const maxCount = Math.max(...(stats?.customers_by_canton || []).map(c => c.count), 1);

  // Filter cantones that have coordinates available
  const cantonesEnMapa = (stats?.customers_by_canton || [])
    .filter(c => CANTON_COORDS[c.canton])
    .map(c => ({
      ...c,
      coords: CANTON_COORDS[c.canton],
      radius: 6 + (c.count / maxCount) * 24,
    }));

  return (
    <div data-testid="dashboard-page" className="fade-in">
      <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Dashboard
          </h2>
          <p className="text-slate-500 text-sm mt-1">Vista general de tu negocio</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <SetupCompleteBadge company={wizardCompany} hasCustomers={hasCustomers} onReopenWizard={() => setWizardOpen(true)} />
          <Link
            to="/dashboard/customize"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border border-slate-200 hover:border-slate-300 rounded-sm text-slate-600 hover:text-[#0B1F3A] hover:bg-slate-50 transition-colors"
            data-testid="customize-dashboard-link"
          >
            <SettingsIcon className="h-3.5 w-3.5" />
            Personalizar
          </Link>
        </div>
      </div>

      {showOnboarding && (
        <WelcomeQuickStart onOpenWizard={() => setWizardOpen(true)} />
      )}

      {wizardCompany && (
        <OnboardingWizard
          open={wizardOpen}
          onOpenChange={setWizardOpen}
          company={wizardCompany}
          onCompanyChange={setWizardCompany}
        />
      )}

      {showOnboarding && (
        <Card
          data-testid="dashboard-onboarding"
          className="mb-6 border border-[#002FA7]/15 rounded-sm overflow-hidden relative"
          style={{
            backgroundImage:
              "radial-gradient(circle at top right, rgb(0 47 167 / 0.06), transparent 55%), linear-gradient(135deg, #ffffff 0%, #F5F7FF 100%)",
          }}
        >
          <button
            type="button"
            onClick={dismissOnboarding}
            data-testid="onboarding-dismiss"
            aria-label="Cerrar onboarding"
            className="absolute top-3 right-3 p-1.5 rounded-sm text-slate-400 hover:text-slate-700 hover:bg-white/70 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="p-6 sm:p-8">
            <div className="flex items-start gap-3 mb-2">
              <div className="p-2.5 rounded-sm bg-[#EEF2FF] text-[#002FA7]">
                <Sparkles className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <h3
                  className="text-lg sm:text-xl font-bold text-slate-900"
                  style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
                >
                  {completedSteps === 0 ? "¡Bienvenido a MiNegocio!" : "¡Vas muy bien!"}
                </h3>
                <p className="text-sm text-slate-600 mt-1 max-w-2xl">
                  {completedSteps === 0
                    ? "Tres pasos para tener tu negocio listo. Podés importar desde CSV o crear uno por uno."
                    : `Llevás ${completedSteps} de 3 pasos. Completá los siguientes para activar todas las funciones.`}
                </p>
              </div>
            </div>
            {/* Progress bar */}
            <div className="mt-4 mb-5">
              <div className="flex items-center justify-between text-xs text-slate-500 mb-1.5">
                <span className="font-semibold tracking-wide">PROGRESO</span>
                <span data-testid="onboarding-progress">{completedSteps} de 3</span>
              </div>
              <div className="h-1.5 w-full bg-white/80 rounded-sm overflow-hidden border border-slate-200/60">
                <div
                  className="h-full bg-[#002FA7] transition-all duration-500 ease-out"
                  style={{ width: `${(completedSteps / 3) * 100}%` }}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { to: "/customers", testid: "onboarding-step-customers", icon: Users, num: 1, title: "Tus clientes", subtitle: "Importá tu cartera o creá uno", done: hasCustomers },
                { to: "/products", testid: "onboarding-step-products", icon: Package, num: 2, title: "Tus productos", subtitle: "Catálogo con precios y variantes", done: hasProducts },
                { to: "/orders", testid: "onboarding-step-orders", icon: ShoppingCart, num: 3, title: "Primer pedido", subtitle: "Manual o con IA desde foto", done: hasOrders },
              ].map(({ to, testid, icon: Icon, num, title, subtitle, done }) => {
                // First pending step is "active"
                const isActive = !done && [hasCustomers, hasProducts, hasOrders].slice(0, num - 1).every(Boolean);
                return (
                  <Link
                    key={testid}
                    to={to}
                    data-testid={testid}
                    data-state={done ? "done" : isActive ? "active" : "pending"}
                    className={`group flex items-center justify-between gap-3 rounded-sm p-4 transition-all ${
                      done
                        ? "bg-emerald-50/70 border border-emerald-200"
                        : isActive
                        ? "bg-white border-2 border-[#002FA7] shadow-sm"
                        : "bg-white border border-slate-200 hover:border-[#002FA7] hover:shadow-sm"
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-sm ${
                          done ? "bg-emerald-600 text-white" : "bg-[#EEF2FF] text-[#002FA7]"
                        }`}
                      >
                        {done ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                      </div>
                      <div className="min-w-0">
                        <p className={`text-sm font-semibold ${done ? "text-emerald-900 line-through" : "text-slate-900"}`}>
                          {num}. {title}
                        </p>
                        <p className="text-xs text-slate-500 truncate">
                          {done ? "Completado" : subtitle}
                        </p>
                      </div>
                    </div>
                    {!done && (
                      <ArrowRight
                        className={`h-4 w-4 shrink-0 transition-all ${
                          isActive
                            ? "text-[#002FA7] translate-x-0.5"
                            : "text-slate-400 group-hover:text-[#002FA7] group-hover:translate-x-0.5"
                        }`}
                      />
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        </Card>
      )}

      {showGraduated && (
        <Card
          data-testid="dashboard-onboarding-graduated"
          className="mb-6 border border-emerald-200 rounded-sm overflow-hidden relative fade-in"
          style={{
            backgroundImage:
              "radial-gradient(circle at 0% 0%, rgb(16 185 129 / 0.10), transparent 50%), linear-gradient(135deg, #ffffff 0%, #ECFDF5 100%)",
          }}
        >
          <button
            type="button"
            onClick={markGraduatedSeen}
            data-testid="onboarding-graduated-dismiss"
            aria-label="Cerrar mensaje"
            className="absolute top-3 right-3 p-1.5 rounded-sm text-emerald-700/60 hover:text-emerald-900 hover:bg-white/70 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="p-6 sm:p-8 flex items-start gap-4">
            <div className="p-3 rounded-full bg-emerald-600 text-white shrink-0">
              <Check className="h-6 w-6" />
            </div>
            <div className="flex-1 min-w-0">
              <h3
                className="text-lg sm:text-xl font-bold text-emerald-900"
                style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
              >
                ¡Listo, tu negocio está configurado!
              </h3>
              <p className="text-sm text-emerald-800/80 mt-1 max-w-2xl">
                Ya cargaste tus clientes, productos y primer pedido. Ahora explorá costeos, rutas y analítica para sacarle el máximo provecho.
              </p>
              <div className="flex items-center gap-3 mt-4 flex-wrap">
                <Link
                  to="/costing"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-800 hover:text-emerald-900 group"
                  data-testid="graduated-cta-costing"
                >
                  Calculá el costeo de un producto
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                </Link>
                <span className="text-emerald-300">•</span>
                <Link
                  to="/routes"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-800 hover:text-emerald-900 group"
                  data-testid="graduated-cta-routes"
                >
                  Planificá una ruta
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </div>
              <p className="text-[11px] text-emerald-700/60 mt-3">
                Este mensaje se ocultará automáticamente en unos segundos.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Widget-driven body — order and visibility from /api/dashboard/widgets */}
      <DashboardWidgets
        stats={stats}
        tasksOpen={tasksOpen}
        setTasksOpen={setTasksOpen}
        pendingTasks={pendingTasks}
        tasksAlta={tasksAlta}
        topCantones={topCantones}
        provinciasData={provinciasData}
        cantonesEnMapa={cantonesEnMapa}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Widget orchestration — reads the saved layout from /api/dashboard/widgets
// and renders ONLY the visible widgets in the user's chosen order.
// Each widget is rendered by a render function that closes over the
// Dashboard's data so we avoid re-fetching everything per widget.
// ─────────────────────────────────────────────────────────────────────────────
function DashboardWidgets({ stats, tasksOpen, setTasksOpen, pendingTasks, tasksAlta, topCantones, provinciasData, cantonesEnMapa }) {
  const [layout, setLayout] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/dashboard/widgets");
        if (alive) setLayout(data.widgets || []);
      } catch {
        if (alive) setLayout([]);
      }
    })();
    const handler = () => {
      api.get("/dashboard/widgets").then(r => setLayout(r.data.widgets || [])).catch(() => {});
    };
    window.addEventListener("dashboard-layout-updated", handler);
    return () => window.removeEventListener("dashboard-layout-updated", handler);
  }, []);

  if (layout === null) {
    return <div className="h-24 rounded-sm bg-slate-100 animate-pulse" data-testid="dashboard-widgets-loading" />;
  }

  const renderers = {
    kpis: () => <KpisWidget stats={stats} />,
    sales_rollup: () => <SalesRollupSection />,
    expenses: () => <ExpensesWidget />,
    tasks: () => <TasksWidget open={tasksOpen} setOpen={setTasksOpen} pendingTasks={pendingTasks} tasksAlta={tasksAlta} />,
    map: () => <MapWidget cantonesEnMapa={cantonesEnMapa} />,
    geo_breakdown: () => <GeoBreakdownWidget topCantones={topCantones} provinciasData={provinciasData} />,
    recent_orders: () => <RecentOrdersWidget orders={stats?.recent_orders} />,
  };

  return (
    <div data-testid="dashboard-widgets-body">
      {layout
        .filter(w => w.visible !== false && renderers[w.key])
        .map(w => (
          <div key={w.key} data-testid={`widget-${w.key}`}>
            {renderers[w.key]()}
          </div>
        ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// KPI cards row
// ─────────────────────────────────────────────────────────────────────────────
function KpisWidget({ stats }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-4 mb-6">
      <Card className="stat-card bg-white border border-slate-200 rounded-sm p-4 sm:p-6" data-testid="stat-customers">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-2">CLIENTES</p>
            <p className="text-xl sm:text-2xl xl:text-3xl font-black tracking-tight truncate">{stats?.total_customers || 0}</p>
          </div>
          <Users className="h-6 w-6 sm:h-8 sm:w-8 text-[rgb(0,47,167)] shrink-0" />
        </div>
      </Card>
      <Card className="stat-card bg-white border border-slate-200 rounded-sm p-4 sm:p-6" data-testid="stat-products">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-2">PRODUCTOS</p>
            <p className="text-xl sm:text-2xl xl:text-3xl font-black tracking-tight truncate">{stats?.total_products || 0}</p>
          </div>
          <Package className="h-6 w-6 sm:h-8 sm:w-8 text-[rgb(0,47,167)] shrink-0" />
        </div>
      </Card>
      <Card className="stat-card bg-white border border-slate-200 rounded-sm p-4 sm:p-6" data-testid="stat-suppliers">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-2">PROVEEDORES</p>
            <p className="text-xl sm:text-2xl xl:text-3xl font-black tracking-tight truncate">{stats?.total_suppliers || 0}</p>
          </div>
          <Truck className="h-6 w-6 sm:h-8 sm:w-8 text-[rgb(0,47,167)] shrink-0" />
        </div>
      </Card>
      <Card className="stat-card bg-white border border-slate-200 rounded-sm p-4 sm:p-6" data-testid="stat-orders">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-2">PEDIDOS</p>
            <p className="text-xl sm:text-2xl xl:text-3xl font-black tracking-tight truncate">{stats?.total_orders || 0}</p>
          </div>
          <ShoppingCart className="h-6 w-6 sm:h-8 sm:w-8 text-[rgb(0,47,167)] shrink-0" />
        </div>
      </Card>
      <Card className="stat-card bg-white border border-slate-200 rounded-sm p-4 sm:p-6" data-testid="stat-revenue">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-2">INGRESOS</p>
            <p className="text-xl sm:text-2xl xl:text-3xl font-black tracking-tight truncate" title={formatMoney(stats?.total_revenue || 0)}>
              {formatMoneyCompact(stats?.total_revenue || 0)}
            </p>
          </div>
          <DollarSign className="h-6 w-6 sm:h-8 sm:w-8 text-[rgb(0,47,167)] shrink-0" />
        </div>
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tasks widget — collapsible list of pending tasks ordered by urgency
// ─────────────────────────────────────────────────────────────────────────────
function TasksWidget({ open, setOpen, pendingTasks, tasksAlta }) {
  return (
      <Card className="bg-white border border-slate-200 rounded-sm mb-6 overflow-hidden" data-testid="dashboard-tasks-widget">
        <Collapsible open={open} onOpenChange={setOpen}>
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="w-full flex items-center justify-between p-5 hover:bg-slate-50 transition-colors text-left"
              data-testid="dashboard-tasks-toggle"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-sm bg-blue-50 text-[rgb(0,47,167)]">
                  <CheckSquare className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                    Tareas Pendientes
                    {pendingTasks.length > 0 && (
                      <span className="text-xs font-bold px-2 py-0.5 bg-[rgb(0,47,167)] text-white rounded-sm">
                        {pendingTasks.length}
                      </span>
                    )}
                    {tasksAlta > 0 && (
                      <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 bg-red-50 text-red-700 border border-red-200 rounded-sm flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {tasksAlta} alta{tasksAlta === 1 ? "" : "s"}
                      </span>
                    )}
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {pendingTasks.length === 0
                      ? "No tenés tareas pendientes. ¡Buen trabajo!"
                      : `${pendingTasks.length} pendiente${pendingTasks.length === 1 ? "" : "s"} ordenadas por urgencia y vencimiento`}
                  </p>
                </div>
              </div>
              <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="border-t border-slate-200">
              {pendingTasks.length === 0 ? (
                <div className="px-5 py-6 text-center text-sm text-slate-500">
                  <Link to="/tasks" className="text-[rgb(0,47,167)] hover:underline font-medium">
                    Crear una nueva tarea →
                  </Link>
                </div>
              ) : (
                <>
                  <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
                    {pendingTasks.slice(0, 10).map(t => {
                      const today = new Date().toISOString().split("T")[0];
                      const isOverdue = t.fecha_vencimiento && t.fecha_vencimiento < today;
                      const urgMeta = {
                        alta: { dot: "bg-red-500", label: "Alta" },
                        media: { dot: "bg-amber-500", label: "Media" },
                        baja: { dot: "bg-blue-500", label: "Baja" },
                      }[t.urgencia] || { dot: "bg-slate-400", label: "Media" };
                      return (
                        <Link
                          key={t.id}
                          to="/tasks"
                          className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50 transition-colors"
                          data-testid="dashboard-task-row"
                        >
                          <span className={`flex-shrink-0 w-2.5 h-2.5 rounded-full ${urgMeta.dot}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-medium text-slate-900 truncate">{t.titulo}</span>
                              {isOverdue && (
                                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border rounded-sm text-red-700 bg-red-50 border-red-200">
                                  Vencida
                                </span>
                              )}
                              {t.estado === "en_progreso" && (
                                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border rounded-sm text-blue-700 bg-blue-50 border-blue-200">
                                  En progreso
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-[11px] text-slate-500 mt-0.5">
                              {t.fecha_vencimiento && (
                                <span className={isOverdue ? "text-red-600 font-medium" : ""}>
                                  📅 {t.fecha_vencimiento}{t.hora_vencimiento ? ` ${t.hora_vencimiento}` : ""}
                                </span>
                              )}
                              {t.recordatorio_at && (
                                <span className="flex items-center gap-1">
                                  <Bell className="h-3 w-3" />
                                  {new Date(t.recordatorio_at).toLocaleString("es-CR", { dateStyle: "short", timeStyle: "short" })}
                                </span>
                              )}
                              <span className="text-slate-400">{urgMeta.label}</span>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                  {pendingTasks.length > 10 && (
                    <div className="px-5 py-3 text-center border-t border-slate-100 bg-slate-50">
                      <Link to="/tasks" className="text-xs font-medium text-[rgb(0,47,167)] hover:underline" data-testid="dashboard-tasks-see-all">
                        Ver las {pendingTasks.length} tareas →
                      </Link>
                    </div>
                  )}
                </>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Map widget — leaflet view of customer cantones
// ─────────────────────────────────────────────────────────────────────────────
function MapWidget({ cantonesEnMapa }) {
  return (
      <Card className="bg-white border border-slate-200 rounded-sm p-6 mb-6" data-testid="map-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xl font-semibold flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              <MapPin className="h-5 w-5 text-[rgb(0,47,167)]" />
              Distribución Geográfica de Clientes
            </h3>
            <p className="text-xs text-slate-500 mt-1">El tamaño del círculo indica la cantidad de clientes por cantón</p>
          </div>
          <span className="text-xs text-slate-400">{cantonesEnMapa.length} cantones con clientes</span>
        </div>
        <div className="border border-slate-200 rounded-sm overflow-hidden" style={{ height: "500px" }}>
          <MapContainer
            center={COSTA_RICA_CENTER}
            zoom={COSTA_RICA_ZOOM}
            style={{ height: "100%", width: "100%" }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {cantonesEnMapa.map((c, idx) => (
              <CircleMarker
                key={idx}
                center={c.coords}
                radius={c.radius}
                pathOptions={{
                  fillColor: "#002FA7",
                  fillOpacity: 0.6,
                  color: "#002FA7",
                  weight: 2,
                }}
              >
                <LeafletTooltip permanent direction="top" offset={[0, -c.radius]}>
                  <div style={{ fontSize: "11px", fontWeight: "600" }}>
                    {c.canton}: {c.count}
                  </div>
                </LeafletTooltip>
                <Popup>
                  <div style={{ fontSize: "13px" }}>
                    <strong>{c.canton}</strong>
                    <br />
                    <span style={{ color: "#64748B" }}>{c.provincia}</span>
                    <br />
                    <span style={{ color: "#002FA7", fontWeight: "bold" }}>
                      {c.count} cliente{c.count !== 1 ? "s" : ""}
                    </span>
                  </div>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>
      </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Geo breakdown — top cantones bar + provincias pie
// ─────────────────────────────────────────────────────────────────────────────
function GeoBreakdownWidget({ topCantones, provinciasData }) {
  return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="bg-white border border-slate-200 rounded-sm p-6">
          <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Top 10 Cantones
          </h3>
          {topCantones.length === 0 ? (
            <p className="text-slate-500 text-center py-12">No hay datos disponibles</p>
          ) : (
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={topCantones} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis
                  type="category"
                  dataKey="canton"
                  tick={{ fontSize: 11 }}
                  width={100}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#fff",
                    border: "1px solid #E2E8F0",
                    borderRadius: "2px",
                  }}
                  formatter={(value) => [`${value} clientes`, "Cantidad"]}
                />
                <Bar dataKey="count" fill="#002FA7" radius={[0, 2, 2, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="bg-white border border-slate-200 rounded-sm p-6">
          <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Distribución por Provincia
          </h3>
          {provinciasData.length === 0 ? (
            <p className="text-slate-500 text-center py-12">No hay datos disponibles</p>
          ) : (
            <ResponsiveContainer width="100%" height={350}>
              <PieChart>
                <Pie
                  data={provinciasData}
                  dataKey="count"
                  nameKey="provincia"
                  cx="50%"
                  cy="50%"
                  outerRadius={120}
                  label={({ provincia, count }) => `${provincia}: ${count}`}
                  labelLine={{ stroke: "#94A3B8" }}
                  style={{ fontSize: "11px" }}
                >
                  {provinciasData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#fff",
                    border: "1px solid #E2E8F0",
                    borderRadius: "2px",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Recent orders widget
// ─────────────────────────────────────────────────────────────────────────────
function RecentOrdersWidget({ orders }) {
  return (
      <Card className="bg-white border border-slate-200 rounded-sm p-6 mb-6" data-testid="dashboard-recent-orders">
        <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          Pedidos Recientes
        </h3>
        {orders && orders.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 font-medium text-slate-600">Cliente</th>
                  <th className="text-left py-3 px-4 font-medium text-slate-600">Vendedor</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-600">Total</th>
                  <th className="text-left py-3 px-4 font-medium text-slate-600">Fecha</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id} className="table-row border-b border-slate-100">
                    <td className="py-3 px-4">{order.nombre_cliente}</td>
                    <td className="py-3 px-4">{order.nombre_vendedor}</td>
                    <td className="py-3 px-4 text-right font-medium">{formatMoney(order.total)}</td>
                    <td className="py-3 px-4 text-slate-500">
                      {new Date(order.created_at).toLocaleDateString('es-ES')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-slate-500 text-center py-8">No hay pedidos recientes</p>
        )}
      </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sales rollup — accumulated sales (this month / 30d / 60d / year)
// ─────────────────────────────────────────────────────────────────────────────
function SalesRollupSection() {
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [rollup, trendRes] = await Promise.all([
          api.get("/dashboard/sales-rollup"),
          api.get("/dashboard/sales-trend?days=30"),
        ]);
        if (!alive) return;
        setData(rollup.data);
        setTrend(trendRes.data?.series || []);
      } catch {
        if (alive) { setData(null); setTrend([]); }
      } finally {
        if (alive) setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <Card className="bg-white border border-slate-200 rounded-sm p-4 mb-6 animate-pulse" data-testid="sales-rollup-loading">
        <div className="h-4 w-40 bg-slate-200 rounded mb-3" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[0,1,2,3].map(i => <div key={i} className="h-20 bg-slate-100 rounded" />)}
        </div>
      </Card>
    );
  }

  if (!data) return null;

  // Slice the 30-day trend into the 4 window views used by the cards.
  const trendSlices = (() => {
    if (!trend || !trend.length) return { month: [], last_30_days: [], last_60_days: [], year: [] };
    const today = new Date();
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
    return {
      month: trend.filter(p => p.date >= monthStart),
      last_30_days: trend,
      last_60_days: trend,     // backend only returns 30d; share the same sparkline visually
      year: trend,
    };
  })();

  const sparkColor = {
    month:        { stroke: "#059669", fill: "#10B98122" },
    last_30_days: { stroke: "#2563EB", fill: "#3B82F622" },
    last_60_days: { stroke: "#4F46E5", fill: "#6366F122" },
    year:         { stroke: "#D97706", fill: "#F59E0B22" },
  };

  const cards = [
    { key: "month", icon: TrendingUp, color: "text-emerald-600", bgAccent: "bg-emerald-50" },
    { key: "last_30_days", icon: TrendingUp, color: "text-blue-600", bgAccent: "bg-blue-50" },
    { key: "last_60_days", icon: TrendingUp, color: "text-indigo-600", bgAccent: "bg-indigo-50" },
    { key: "year", icon: TrendingUp, color: "text-amber-600", bgAccent: "bg-amber-50" },
  ];

  return (
    <div className="mb-6" data-testid="sales-rollup-section">
      <div className="flex items-baseline justify-between mb-3 px-1">
        <h2 className="text-base sm:text-lg font-black tracking-tight" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
          Ventas acumuladas
        </h2>
        <Link to="/invoices" className="text-xs text-slate-500 hover:text-[#0B1F3A] inline-flex items-center gap-0.5" data-testid="sales-rollup-link-invoices">
          Ver facturas <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="sales-rollup-grid">
        {cards.map(({ key, icon: Icon, color, bgAccent }) => {
          const row = data[key];
          if (!row) return null;
          const points = trendSlices[key] || [];
          const sk = sparkColor[key];
          // Ensure at least 2 points so AreaChart renders a visible curve
          const sparkData = points.length > 1 ? points : (points.length === 1 ? [...points, ...points] : []);
          return (
            <Card
              key={key}
              className="bg-white border border-slate-200 rounded-sm p-4 hover:border-slate-300 transition-colors"
              data-testid={`sales-rollup-card-${key}`}
            >
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <span className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold truncate">
                  {row.label}
                </span>
                <span className={`shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-sm ${bgAccent}`}>
                  <Icon className={`h-3.5 w-3.5 ${color}`} />
                </span>
              </div>
              <div
                className="text-xl sm:text-2xl xl:text-3xl font-black truncate"
                style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
                title={formatMoney(row.total)}
                data-testid={`sales-rollup-amount-${key}`}
              >
                {formatMoneyCompact(row.total)}
              </div>
              {/* Net sales line (gross minus returns). Only render when at
                  least one return exists in this window, otherwise the
                  "net" view is identical to gross and would be noise. */}
              {(row.returns_total || 0) > 0 && (
                <div className="flex items-center justify-between gap-2 mt-1 -mb-0.5" data-testid={`sales-rollup-net-${key}`}>
                  <span className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Neto</span>
                  <span className="text-xs font-bold text-emerald-700 truncate" title={formatMoney(row.net_total)}>
                    {formatMoneyCompact(row.net_total)}
                  </span>
                </div>
              )}
              {(row.returns_total || 0) > 0 && (
                <div className="flex items-center justify-between gap-2" data-testid={`sales-rollup-returns-${key}`}>
                  <span className="text-[10px] uppercase tracking-wide text-rose-600 font-semibold">Devoluc.</span>
                  <span className="text-xs font-bold text-rose-600 truncate" title={formatMoney(row.returns_total)}>
                    -{formatMoneyCompact(row.returns_total)}
                  </span>
                </div>
              )}
              {/* Sparkline of daily sales for this window */}
              {sparkData.length >= 2 && (
                <div className="h-10 mt-1 -mx-1" data-testid={`sales-rollup-spark-${key}`}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={sparkData} margin={{ top: 2, right: 2, left: 2, bottom: 0 }}>
                      <defs>
                        <linearGradient id={`grad-${key}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={sk.stroke} stopOpacity={0.35} />
                          <stop offset="100%" stopColor={sk.stroke} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <Tooltip
                        cursor={{ stroke: sk.stroke, strokeOpacity: 0.3 }}
                        contentStyle={{ fontSize: "11px", border: "1px solid #E2E8F0", borderRadius: 2, padding: "4px 8px" }}
                        labelFormatter={(label) => label}
                        formatter={(value) => [formatMoney(value), "Ventas"]}
                      />
                      <Area
                        type="monotone"
                        dataKey="total"
                        stroke={sk.stroke}
                        strokeWidth={1.6}
                        fill={`url(#grad-${key})`}
                        isAnimationActive={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
              <div className="text-xs text-slate-500 mt-1">
                {row.count} factura{row.count === 1 ? "" : "s"} · desde {row.since}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Expenses widget — compact summary card (30d totals + month vs month + top cats)
// ─────────────────────────────────────────────────────────────────────────────
function ExpensesWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get("/expenses/summary");
        if (alive) setData(res.data);
      } catch {
        if (alive) setData(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <Card className="bg-white border border-slate-200 rounded-sm p-6 mb-6">
        <div className="h-24 animate-pulse bg-slate-50 rounded-sm" />
      </Card>
    );
  }

  const fmt = (n, cur) =>
    new Intl.NumberFormat("es-CR", {
      style: "currency",
      currency: cur,
      maximumFractionDigits: cur === "USD" ? 2 : 0,
    }).format(n || 0);

  const cur = data?.current_month || { CRC: 0, USD: 0, count: 0 };
  const prev = data?.previous_month || { CRC: 0, USD: 0, count: 0 };
  const pct = (a, b) => {
    if (!b) return a > 0 ? 100 : 0;
    return Math.round(((a - b) / b) * 100);
  };
  const pctCRC = pct(cur.CRC, prev.CRC);
  const pctUSD = pct(cur.USD, prev.USD);
  const topCats = data?.top_categories_30d || [];
  const recent = data?.recent || [];
  const empty = (data?.last_30_days?.count || 0) === 0;

  return (
    <Card className="bg-white border border-slate-200 rounded-sm mb-6 overflow-hidden" data-testid="dashboard-expenses-widget">
      <div className="flex items-center justify-between p-5 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-sm bg-rose-50 text-rose-600">
            <Wallet className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-semibold" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>Gastos recientes</h3>
            <p className="text-xs text-slate-500">Últimos 30 días</p>
          </div>
        </div>
        <Link
          to="/expenses"
          className="text-xs text-[rgb(0,47,167)] hover:underline flex items-center gap-1"
          data-testid="dashboard-expenses-link"
        >
          Ver todos <ArrowRight className="h-3 w-3" />
        </Link>
      </div>

      {empty ? (
        <div className="p-8 text-center">
          <Wallet className="h-10 w-10 mx-auto mb-2 text-slate-300" />
          <p className="text-sm text-slate-500">Aún no registraste gastos.</p>
          <Link
            to="/expenses"
            className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-[rgb(0,47,167)] hover:underline"
            data-testid="dashboard-expenses-add-link"
          >
            Registrar el primero <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      ) : (
        <div className="p-5 space-y-5">
          {/* KPIs: CRC + USD totals con %variación */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="expenses-kpis">
            <div className="p-4 border border-slate-200 rounded-sm">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] uppercase tracking-wider text-slate-500">Mes en curso (CRC)</span>
                {prev.CRC > 0 && (
                  <span className={`text-[11px] font-semibold flex items-center gap-0.5 ${pctCRC >= 0 ? "text-rose-600" : "text-emerald-600"}`}>
                    {pctCRC >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                    {pctCRC > 0 ? "+" : ""}{pctCRC}%
                  </span>
                )}
              </div>
              <div className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Cabinet Grotesk" }}>
                {fmt(cur.CRC, "CRC")}
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                Mes anterior: {fmt(prev.CRC, "CRC")}
              </div>
            </div>
            <div className="p-4 border border-slate-200 rounded-sm">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] uppercase tracking-wider text-slate-500">Mes en curso (USD)</span>
                {prev.USD > 0 && (
                  <span className={`text-[11px] font-semibold flex items-center gap-0.5 ${pctUSD >= 0 ? "text-rose-600" : "text-emerald-600"}`}>
                    {pctUSD >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                    {pctUSD > 0 ? "+" : ""}{pctUSD}%
                  </span>
                )}
              </div>
              <div className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Cabinet Grotesk" }}>
                {fmt(cur.USD, "USD")}
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                Mes anterior: {fmt(prev.USD, "USD")}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Top categorías */}
            <div>
              <h4 className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
                Top categorías · 30 días
              </h4>
              {topCats.length === 0 ? (
                <p className="text-xs text-slate-400 italic">Sin datos</p>
              ) : (
                <ul className="space-y-1.5" data-testid="expenses-top-cats">
                  {topCats.map((c) => (
                    <li
                      key={c.categoria}
                      className="flex items-center justify-between py-1.5 px-2 rounded-sm hover:bg-slate-50 text-sm"
                    >
                      <span className="font-medium text-slate-700 truncate">{c.categoria}</span>
                      <span className="text-xs text-slate-600 flex-shrink-0 ml-2">
                        {c.total_crc > 0 && <span className="mono">{fmt(c.total_crc, "CRC")}</span>}
                        {c.total_crc > 0 && c.total_usd > 0 && <span className="mx-1.5 text-slate-300">·</span>}
                        {c.total_usd > 0 && <span className="mono">{fmt(c.total_usd, "USD")}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {/* Recientes */}
            <div>
              <h4 className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
                Últimos movimientos
              </h4>
              {recent.length === 0 ? (
                <p className="text-xs text-slate-400 italic">Sin datos</p>
              ) : (
                <ul className="space-y-1.5" data-testid="expenses-recent-list">
                  {recent.map((e) => (
                    <li key={e.id} className="flex items-center justify-between py-1.5 px-2 rounded-sm hover:bg-slate-50">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-700 truncate">{e.descripcion}</div>
                        <div className="text-[10px] text-slate-500">
                          {e.fecha} · {e.metodo_pago} · {e.categoria}
                        </div>
                      </div>
                      <span className="text-sm font-semibold mono text-rose-600 flex-shrink-0 ml-2">
                        −{fmt(e.monto, e.moneda)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
